<template lang="pug">
.other-facilities-application-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません

    // セル：申込み部屋
    template(#cell(rooms)="row") 
      span {{ getRooms(row.item) }}
    // セル：ステップ
    template(#cell(step)="row") 
      span {{ convertStatusName(row.item.status) }}
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { ResidentStatus } from "@/commons/services/Residents";
import {
  TemplateItem,
  TemplateItemGroup,
  TemplateItemInput,
  TemplateItemRadioGroup,
  TemplateItemRoomType,
  TemplateItemSection,
  TemplateItemSwitchCheckbox,
  TemplateType
} from "@/commons/services/AdmissionApplicationTemplates";
import { WaitersGetModel } from "@/commons/services/Waiters";

/**
 * ### 他施設の申込み状況テーブルコンポーネント
 */
@Component({})
export default class OtherFacilitiesApplicationTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<OtherFacilitiesApplicationTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<OtherFacilitiesApplicationTableField>;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;

  get _value(): Array<OtherFacilitiesApplicationTableItem> {
    return this.value;
  }

  public convertStatusName(status: ResidentStatus): string {
    let statusName: string = "";

    switch (status) {
      case ResidentStatus.REGISTERD:
        statusName = "登録者";
        break;
      case ResidentStatus.CANCELED:
        statusName = "取り下げ";
        break;
      case ResidentStatus.TEMPORARILY:
        statusName = "一時保存";
        break;
      case ResidentStatus.APPLICATION:
        statusName = "入所申込み";
        break;
      case ResidentStatus.RECEIVED_APPLICATION:
        statusName = "申込み受理";
        break;
      case ResidentStatus.COMPLETE_APPLICATION:
        statusName = "判定済";
        break;
      case ResidentStatus.CONFIRM_ADMISSION:
        statusName = "入所意向確認";
        break;
      case ResidentStatus.FINAL_JUDGE:
        statusName = "最終判定中";
        break;
      case ResidentStatus.COMPLETE_FINAL_JUDGE:
        statusName = "最終判定済";
        break;
      case ResidentStatus.ADMISSION_PROCEDURE:
        statusName = "入所手続き";
        break;
      case ResidentStatus.RESIDENT:
        statusName = "利用者";
        break;
      default:
        statusName = "";
        break;
    }

    return statusName;
  }

  public getRooms(item: OtherFacilitiesApplicationTableItem): string {
    let rooms: string[] = [];

    const roomType: TemplateItemRoomType | null = this.serachTemplateProperty(item.waiter.admissionApplication.templateAnswer.properties, "roomType") as TemplateItemRoomType;

    if (roomType == null) {
      return "";
    }

    for (const answer of roomType.answer) {
      if (item.facilityId == answer.facilityId) {
        if (answer.recruitmentRoom.multiBed == true) {
          rooms.push("従来型多床室");
        }
        if (answer.recruitmentRoom.conventinalSingle == true) {
          rooms.push("従来型個室");
        }
        if (answer.recruitmentRoom.unitTypeSingle == true) {
          rooms.push("ユニット型個室");
        }
        if (answer.recruitmentRoom.single == true) {
          rooms.push("個室");
        }
        if (answer.recruitmentRoom.double == true) {
          rooms.push("２人部屋（夫婦部屋）");
        }
      }

      if (answer.customRecruitmentRoom && item.facilityId == answer.facilityId) {
        for (const customRoom of answer.customRecruitmentRoom) {
          if (customRoom.use == true) {
            rooms.push(customRoom.name);
          }
        }
      }
    }

    return rooms.join("、");
  }

  public serachTemplateProperty(properties: Array<TemplateItem>, idName: string): TemplateItemInput | null {
    for (const property of properties) {
      // 配下にperopetiesがあれば再帰検索
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const section: TemplateItemSection = property as TemplateItemSection;
          const item: TemplateItemInput | null = this.serachTemplateProperty(section.properties, idName);
          if (item != null) {
            return item;
          }
        } else if (property.type == TemplateType.GROUP) {
          const group: TemplateItemGroup = property as TemplateItemGroup;
          for (const childProperties of group.properties) {
            const item: TemplateItemInput | null = this.serachTemplateProperty(childProperties, idName);
            if (item != null) {
              return item;
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const radioGroup: TemplateItemRadioGroup = property as TemplateItemRadioGroup;
          for (const childProperties of radioGroup.properties) {
            const item: TemplateItemInput | null = this.serachTemplateProperty(childProperties.properties, idName);
            if (item != null) {
              return item;
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const switchCheckbox: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;
          const item: TemplateItemInput | null = this.serachTemplateProperty(switchCheckbox.properties, idName);
          if (item != null) {
            return item;
          }
        }
      }

      if (property.id == idName) {
        return property as TemplateItemInput;
      }
    }
    return null;
  }

  public hasProperties(item: TemplateItem): boolean {
    switch (item.type) {
      case TemplateType.SECTION:
      case TemplateType.GROUP:
      case TemplateType.RADIO_GROUP:
      case TemplateType.SWITCH_CHECKBOX: {
        return true;
      }
      default: {
        return false;
      }
    }
  }
}

/** テーブルの列名の情報 */
export interface OtherFacilitiesApplicationTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface OtherFacilitiesApplicationTableItem extends WaitersGetModel {
  /** 施設名 */
  facilityName: string;
}
</script>
